import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { PlusIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';
import { useDroppable } from '@dnd-kit/react';

import { Tooltip } from '../../../../components/tooltip/Tooltip';

type TaskColumnProps = {
  id: string;
  name: string;
  count: number;
  icon?: React.ReactNode;
  onCreate?: () => void;
  children?: React.ReactNode;
};

/**
 * A board column. This is a droppable container only — columns cannot be
 * reordered (their order is fully determined by the active grouping). Cards
 * are dropped here via the dnd-kit droppable API; empty columns remain valid
 * drop targets. The header `+` opens the create-task modal pre-filled for this
 * column (mirrors the list's `TaskCollection`).
 */
export const TaskColumn: React.FC<TaskColumnProps> = observer(
  ({ id, name, count, icon, onCreate, children }) => {
    const { ref, isDropTarget } = useDroppable({ id, type: 'column', accept: 'item' });

    return (
      <Container data-drop-target={isDropTarget}>
        <Header>
          <HeaderLeft>
            {icon}
            <Name>{name}</Name>
            <Count>{count}</Count>
          </HeaderLeft>

          {onCreate && (
            <Tooltip text="Create task">
              <IconButton icon={<PlusIcon />} onClick={onCreate} />
            </Tooltip>
          )}
        </Header>

        <List ref={ref}>{children}</List>
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 32rem;
  min-width: 32rem;
  max-width: 32rem;
  height: 100%;
  scroll-snap-align: start;
  background-color: var(--color-surface-secondary);
  border-radius: var(--card-border-radius);
  transition: box-shadow 0.2s ease;

  &[data-drop-target='true'] {
    box-shadow: inset 0 0 0 0.1rem var(--color-surface-further);
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: 1rem 1.2rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--color-texts-high-contrast);
  user-select: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Count = styled.span`
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--color-texts-low-contrast);
  user-select: none;
  margin-inline-start: 0.4rem;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 8rem;
  overflow-y: auto;
  padding: 0 0.8rem 0.8rem;
`;
